import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  AuthUserLike,
  NavAccess,
  accessForPath,
  canAccessNav,
  isFirmAdmin,
  userHasPermission,
  userHasRole,
} from '../../utils/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Shorthand: require firm admin (or platform admin). */
  requireAdmin?: boolean;
  /** Require platform super-admin flag. */
  requirePlatformAdmin?: boolean;
  /** Require one of these roles. */
  requiredRoles?: string[];
  /** Require at least one of these permissions. */
  requiredPermissions?: string[];
  /** Explicit access rule. */
  access?: NavAccess;
  /** Where to send unauthorized authenticated users. */
  fallbackPath?: string;
}

function isAuthorized(
  user: AuthUserLike,
  {
    requireAdmin,
    requirePlatformAdmin,
    requiredRoles,
    requiredPermissions,
    access,
  }: Omit<ProtectedRouteProps, 'children' | 'fallbackPath'>
): boolean {
  if (!user) return false;

  if (access) {
    return canAccessNav(user, access);
  }

  if (requirePlatformAdmin) {
    return Boolean(user.isPlatformAdmin);
  }

  if (requireAdmin) {
    return isFirmAdmin(user) || Boolean(user.isPlatformAdmin);
  }

  if (requiredRoles?.length && !userHasRole(user, requiredRoles)) {
    return false;
  }

  if (requiredPermissions?.length) {
    return requiredPermissions.some((p) => userHasPermission(user, p));
  }

  return true;
}

function hasExplicitAccess(
  props: Omit<ProtectedRouteProps, 'children' | 'fallbackPath'>
): boolean {
  return Boolean(
    props.access
      || props.requireAdmin
      || props.requirePlatformAdmin
      || props.requiredRoles?.length
      || props.requiredPermissions?.length
  );
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallbackPath = '/dashboard',
  ...accessProps
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'client') {
    return <Navigate to="/portal/login" replace />;
  }

  const authorized = hasExplicitAccess(accessProps)
    ? isAuthorized(user, accessProps)
    : canAccessNav(user, accessForPath(location.pathname));

  if (!authorized) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
